import { useContext, useEffect, useState } from "react";
import {
  GetDestinationsQuery,
  GetDestinationsQueryVariables,
  useGetDestinationsQuery,
} from "../../../../../generated/graphql";
import { AsyncSelect } from "../../async-select/AsyncSelect";
import { AsyncSelectData } from "../../async-select/AsyncSelectData";
import { useTranslation } from "react-i18next";
import AuthContext from "src/app/contexts/auth-context";

interface FormikFormSelectProps {
  fieldName: string;
  labelName?: string;
  intialId?: number;
  formik;
  type?: string;
  showLabel?:boolean;
}

type DestinationNode = NonNullable<
  NonNullable<GetDestinationsQuery["destinations"]>["nodes"]
>[number];

const FormikDestinationFormSelect = (props: FormikFormSelectProps) => {
  const userService = useContext(AuthContext);
  const [searchInput, setSearchInput] = useState<string>("");
  const [debouncedSearchInput, setDebouncedSearchInput] = useState<string>("");
  const [allDestinations, setAllDestinations] = useState<DestinationNode[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMoreDestinations, setLoadingMoreDestinations] = useState(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchInput(searchInput.trim());
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchInput]);

  const companyId = Number(userService.authContextData?.currentUser?.companyId);
  const destinationQueryVariables: GetDestinationsQueryVariables = {
    companyId,
    take: 15,
    cursor: undefined,
     search: debouncedSearchInput ?? ""
  };

  const { data: destinations, loading: loadingDestinations, fetchMore } = useGetDestinationsQuery({
      variables: destinationQueryVariables,
      skip: !Number.isFinite(companyId) || companyId <= 0,
    }
  );

  const formikSetFieldValue = props.formik.setFieldValue;
  const { t } = useTranslation();

  const [initialDestination, setInitialDestination] = useState<AsyncSelectData>();

  useEffect(() => {
    if (destinations?.destinations) {
      setAllDestinations(destinations.destinations.nodes || []);
      setHasMore(destinations.destinations.pageInfo?.hasNextPage || false);
      setCursor(destinations.destinations.pageInfo?.endCursor || null);
    }
  }, [destinations]);

  useEffect(() => {
    if (allDestinations.length > 0) {
      let destination = allDestinations.find(
        (i) => i.id === props.intialId
      );
      if (destination) {
        let destinationSelectData = new AsyncSelectData({
          title: destination?.name?? "",
          value: destination?.id,
        });
        setInitialDestination(destinationSelectData);
        setTimeout(()=>{
          formikSetFieldValue(props.fieldName, destination?.id??0);
        },200);
      } else {
        setInitialDestination(undefined);
        formikSetFieldValue(props.fieldName, 0);
      }
    }
  }, [
    setInitialDestination,
    allDestinations,
    formikSetFieldValue,
    props.fieldName,
    props.intialId,
  ]);

  const selectedDestinationChanged = (data: AsyncSelectData) => {
    formikSetFieldValue(props.fieldName, data.value);
  };

  const handleSearchInput = (_event: any, value: string) => {
    setSearchInput(value);
  };

  const handleLoadMore = async () => {
    if (hasMore && cursor && !loadingDestinations && !loadingMoreDestinations) {
      const loadMoreVariables: GetDestinationsQueryVariables = {
        companyId,
        take: 15,
        cursor,
         search: debouncedSearchInput ?? ""
      };

      try {
        setLoadingMoreDestinations(true);
        await fetchMore({
          variables: loadMoreVariables,
        });
      } finally {
        setLoadingMoreDestinations(false);
      }
    }
  };

    return (
      <>
        <div className={`mt-3`}>
          <AsyncSelect
            label={props.showLabel ? t("upsert.company.labels.destination") : ""}
            onItemSelected={selectedDestinationChanged}
            inputValueChanged={handleSearchInput}
            onLoadMore={handleLoadMore}
            loadingMoreData={loadingMoreDestinations}
            defaultSelectOption={initialDestination}
            data={allDestinations.map(
              (i) =>
                new AsyncSelectData({
                  title: i?.name?? "",
                  value: i?.id,
                })
            )}
            loadingData={loadingDestinations}
            hasError={Boolean(
              props.formik.errors[props.fieldName] &&
                props.formik.touched[props.fieldName]
            )}
            className={`w-full`}
          />
        </div>
      </>
    );
};

export { FormikDestinationFormSelect };
